import { ReviewFormData } from "@pages/CalendarsPage/SchedulingSection/form.types";
import MeetingDateSelectionSection from "@pages/CreateMeetingInvitePage/components/MeetingDateSelectionSection";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReviewProposalSection from "./components/ReviewProposalSection";
import { IGetInterviewByIdResponse, Meeting } from "@endpoints/types/interview.type";
import {
  getInterviewDetails,
  updateInterview,
} from "@endpoints/interview.service";
import { availabilitySlotsForApiPayload } from "@pages/CreateMeetingInvitePage/CreateMeetingInvitePage";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";
import { EnumUserTypes, userTypes } from "@utils/constants";
import { CalendarContext } from "@pages/CalendarsPage/MeetingLayout";

const CounterProposalPollPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const meetingDateSelectionRef = useRef(null);
  const { user } = useSelector((state: RootState) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const isReviewProposal = searchParams.get("isReviewProposal") === "true";
  const interviewId = searchParams.get("id");

  const [formData, setFormData] = useState<ReviewFormData>();
  const [events, setEvents] = useState({});
  const [activeWeeks, setActiveWeeks] = useState();
  const [availabilities, setAvailabilities] = useState([]);
  const [unAvailabilities, setUnAvailabilities] = useState([]);
  const [interviewDetails, setInterviewDetails] = useState<IGetInterviewByIdResponse>();
  const [isSendingProposal, setIsSendingProposal] = useState(false);

  const fetchInterviewDetails = async () => {
    try {
      const { success, data } = await getInterviewDetails(interviewId);
      if (success) setInterviewDetails(data);
    } catch (error) {
      console.error("Error: fetching interview details", error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const addParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    });
  };

  const handleReviewProposal = async () => {
    if (isReviewProposal) {
      try {
        setIsSendingProposal(true);
        const apiPayload = {
          generalAvailabilitySlots: availabilitySlotsForApiPayload(
            formData.availabilitySlots
          ),
        };
        const { success } = await updateInterview(interviewId, apiPayload);
        if (success) {
          enqueueSnackbar("Proposal sent successfully.", {
            variant: "success",
          });
          // if (user?.userType === userTypes.CANDIDATE) {
          //   navigate("/candidate-calendar/scheduling");
          // } else {
          // }
          navigate("/calendar/meeting/scheduling");
        }
      } catch (error) {
        console.error("Error: sending counter proposal", error);
      } finally {
        setIsSendingProposal(false);
      }
    } else {
      try {
        const data = await meetingDateSelectionRef?.current?.submit();
        setFormData((prev) => {
          return { ...prev, ...data };
        });
        addParam("isReviewProposal", "true");
      } catch (error) {
        console.error("Error: form invalid", error);
      }
    }
  };

  const isMeetingDurationDisabled = useMemo(
    () =>
      !(
        (user?.userType === EnumUserTypes.HIRING_COMPANY &&
          // @ts-ignore
          interviewDetails?.hiringCompanyId?._id) ||
        (user?.userType === EnumUserTypes.RECRUITER &&
          // @ts-ignore
          !interviewDetails?.hiringCompanyId?._id)
      ),
    [interviewDetails, user]
  );

  useEffect(() => {
    if (interviewId) {
      fetchInterviewDetails();
    }
  }, [interviewId]);

  return (
    <CalendarContext.Provider
      value={{
        activeWeeks,
        setActiveWeeks,
        events,
        setEvents,
        availabilities,
        setAvailabilities,
        unAvailabilities,
        setUnAvailabilities,
      }}
    >
      <div
        className={`flex flex-col flex-1 items-center bg-white rounded-md p-6`}
      >
        <div
          className={`${
            isReviewProposal ? "w-[75%] min-h-0 flex flex-col flex-1" : "w-full"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-center relative">
            <button
              onClick={handleBack}
              className="font-semibold text-sm py-2 px-4 bg-primary rounded-md text-white hover:bg-black absolute left-0"
            >
              Back{isReviewProposal}
            </button>
            <span className="font-semibold">
              {isReviewProposal ? "Review" : "Counter"} Proposal Poll
            </span>
            <button
              onClick={handleReviewProposal}
              className="flex items-center gap-3 font-semibold text-sm py-2 px-4 bg-primary rounded-md text-white hover:bg-black absolute right-0"
            >
              {isReviewProposal ? "Send Proposal" : "Review Proposal"}
              {isSendingProposal ? (
                <LoadingAnimation className="'w-4 h-4" />
              ) : null}
            </button>
          </div>

          {!isReviewProposal ? (
            <MeetingDateSelectionSection
              ref={meetingDateSelectionRef}
              isMeetingDurationDisabled={isMeetingDurationDisabled}
              formData={formData}
            />
          ) : (
            <ReviewProposalSection
              formData={formData}
              interviewDetails={interviewDetails}
            />
          )}
        </div>
      </div>
    </CalendarContext.Provider>
  );
};

export default CounterProposalPollPage;
